import { useState } from 'react';
import { useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { UPDATE_CUSTOM_CATEGORY, DELETE_CUSTOM_CATEGORY } from '../../../customer/queries.ts';
import { useToaster } from '../../../core/components/Toast.tsx';

interface PausableCategory {
    id: string;
    enabled: boolean;
}
export const useCategoryOperations = <T extends PausableCategory>(refetch: () => Promise<any>) => {
    const { t } = useTranslation('mailboxes');
    const { publishToast } = useToaster();

    const [categoryToDelete, setCategoryToDelete] = useState<T | null>(null);
    const [pendingActionId, setPendingActionId] = useState<string | null>(null);

    const [updateCustomCategory, { loading: pausing }] = useMutation(UPDATE_CUSTOM_CATEGORY);
    const [deleteCustomCategory, { loading: deleting }] = useMutation(DELETE_CUSTOM_CATEGORY);

    const handleTogglePause = async (category: T) => {
        setPendingActionId(category.id);
        try {
            await updateCustomCategory({ variables: { id: category.id, enabled: !category.enabled } });
            await refetch();
            publishToast({
                description: t(
                    category.enabled ? 'detail.categoriesTab.pauseSuccess' : 'detail.categoriesTab.resumeSuccess'
                ),
                status: 'success',
            });
        } catch (error) {
            console.error(error);
            publishToast({ description: t('detail.categoriesTab.saveError'), status: 'error' });
        } finally {
            setPendingActionId(null);
        }
    };

    const handleDelete = async () => {
        if (!categoryToDelete) return;
        try {
            await deleteCustomCategory({ variables: { id: categoryToDelete.id } });
            await refetch();
            publishToast({ description: t('detail.categoriesTab.deleteSuccess'), status: 'success' });
        } catch (error) {
            console.error(error);
            publishToast({ description: t('detail.categoriesTab.saveError'), status: 'error' });
        } finally {
            setCategoryToDelete(null);
        }
    };

    return {
        categoryToDelete,
        setCategoryToDelete,
        pendingActionId,
        pausing,
        deleting,
        handleTogglePause,
        handleDelete,
    };
};

export default useCategoryOperations;
